import styled from "@mui/material/styles/styled";
import { Chip } from "@mui/material";
import {
  AssignedTo,
  CsobFormMetaDataDto,
  QuestionnaireType,
  UserRole,
} from "@green0meter/api-client";

import { ESRS_COLORS } from "@components/main/csrd/DoubleMateriality/components/Stakeholders/components/ESRS.constants";

// TODO this is hardcoded, should be moved to a theme and define the colors with designer and designer should use it for next design!!!
const colorSchemas = {
  lightBlue: { background: "#E3F2FD", border: "#BBDEFB", color: "#1E88E5" },
  darkBlue: { background: "#C6E0FF", border: "#67ACFF", color: "#0A5CC4" },
  outlineBlue: { background: "#E9EBFB", border: "#C7CCF6", color: "#1E3FC7" },
  lightGreen: { background: "#E6F5ED", border: "#C4E5D2", color: "#2B8B58" },
  darkGreen: { background: "#E2F4F2", border: "#B7E3DD", color: "#269483" },
  darkPurple: { background: "#EEE8FF", border: "#D2C8F7", color: "#5C44CA" },
  lightPurple: { background: "#F3E5FF", border: "#E3C2FE", color: "#A66AD7" },
  lightYellow: { background: "#F5E6B3", border: "#FFD751", color: "#BD9200" },
  darkYellow: { background: "#F5E6B3", border: "#F3C186", color: "#DE8217" },
  red: { background: "#FFEBEE", border: "#FFCDD2", color: "#D32F2F" },
  brown: { background: "#EDE9E8", border: "#D7CCC8", color: "#795548" },
  grey: { background: "#F5F5F5", border: "#E9E9E9", color: "#757575" },
  orange: { background: "#FFF4E4", border: "#FFDBB8", color: "#F57C00" },
  pink: {
    background: "#FFEAF4",
    border: "#FFB8D2",
    color: "#D81B60",
  },
};

export const chipColors: Partial<
  Record<
    CsobFormMetaDataDto["questionnaireType"] | string | UserRole | AssignedTo,
    { background: string; border: string; color: string }
  >
> = {
  // Input types
  Checkbox: colorSchemas.lightBlue,
  DatePicker: colorSchemas.lightGreen,
  Email: colorSchemas.darkBlue,
  Files: colorSchemas.darkPurple,
  MultiInput: colorSchemas.lightYellow,
  HotelsMultiInput: colorSchemas.lightYellow,
  Number: colorSchemas.outlineBlue,
  Radio: colorSchemas.red,
  RichTextBox: colorSchemas.lightPurple,
  Select: colorSchemas.darkGreen,
  Slider: colorSchemas.brown,
  Table: colorSchemas.grey,
  ShortText: colorSchemas.orange,
  // Unit types
  Gj: colorSchemas.outlineBlue,
  kWh: colorSchemas.orange,
  kg: colorSchemas.darkGreen,
  km: colorSchemas.lightBlue,
  l: colorSchemas.darkPurple,
  m3: colorSchemas.brown,
  t: colorSchemas.grey,
  tCO2e: colorSchemas.lightPurple,
  // Roles / AssignedTos
  [UserRole.Owner || AssignedTo.OwnerAndManagers]: colorSchemas.lightBlue,
  [UserRole.Manager || AssignedTo.Everyone]: colorSchemas.orange,
  [UserRole.Respondent || AssignedTo.OnlyOwner]: colorSchemas.darkGreen,
  [UserRole.Viewer || AssignedTo.Hidden]: colorSchemas.grey,
  [UserRole.Admin || AssignedTo.Mixed]: colorSchemas.pink,
  [UserRole.Auditor || AssignedTo.Mixed]: colorSchemas.darkPurple,
  // stakeholders iro
  BusinessAssociation: {
    background: "#C6E0FF",
    border: "#67ACFF",
    color: "#065CC4",
  },
  Impacts: {
    background: "#F9F5FF",
    border: "#E9D7FE",
    color: "#6941C6",
  },
  // Impact Areas
  Risks: colorSchemas.darkYellow,
  Opportunities: colorSchemas.lightGreen,
  Impact: colorSchemas.grey,
  // Stakeholders type
  All: { background: "#E2F4F2", border: "#B7E3DD", color: "#269483" },
  ImpactedCommunity: { background: "#FEFAE3", border: "#FFDEC0", color: "#DC893D" },
  OwnWorkers: { background: "#F9F5FF", border: "#E9D7FE", color: "#6941C6" },
  WorkersInSupplyChain: {
    background: "#E6F5ED",
    border: "#C4E5D2",
    color: "#2B8B58",
  },
  Customers: { background: "#F5E6B3", border: "#F3C186", color: "#DE8217" },
  Finance: { background: "#FFEBEE", border: "#FFCDD2", color: "#D32F2F" },
  ClimateChange: { background: "#E9EBFB", border: "#C7CCF6", color: "#1E3FC7" },
  Water: { background: "#C6E0FF", border: "#67ACFF", color: "#065CC4" },
  Biodiversity: { background: "#F3E4FF", border: "#E3C2FE", color: "#A66AD7" },
  Circularity: { background: "#FAF6A7", border: "#F0E9AC", color: "#807A00" },
  Pollution: { background: "#EDD9CF", border: "#D6AB97", color: "#AE7054" },
  Communities: { background: "#D6F7F3", border: "#9DD3DA", color: "#2B818D" },
  Governance: { background: "#FFF4E4", border: "#FFE2BB", color: "#FA963B" },
  Others: { background: "#F5F5F5", border: "#E9E9E9", color: "#555555" },
  Language: { background: "#FCFCFC", border: "#E9E9E9", color: "#555555" },
  ...ESRS_COLORS,
  // Asset Questionnaire types
  client: colorSchemas.lightPurple,
  commercial_reality_asset: colorSchemas.lightBlue,
  energy_asset: colorSchemas.orange,
};

interface StyledChip {
  type: QuestionnaireType | string;
}

export const StyledChip = styled(Chip)<StyledChip>(({ type }) => {
  const styles = chipColors[type];

  if (!styles) {
    return {};
  }

  return {
    backgroundColor: styles.background,
    border: `1px solid ${styles.border}`,
    color: styles.color,

    "& .MuiChip-deleteIcon": {
      color: styles.color,
      marginLeft: "2px",
      marginRight: "-4px",
      "&:hover": {
        color: styles.color,
        opacity: 0.7,
      },
    },
  };
});
